import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";

const MODES = ["same_meal_for_all", "child_alternative", "diet_alternative", "optional_second_recipe"];
const GROUPS = ["all", "adults", "children", "one_person", "custom"];
const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function numberOrNull(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatMoney(value, currency = "EUR") {
  if (value === null || value === undefined || value === "") return "";
  const symbol = currency === "USD" ? "$" : "€";
  return `${symbol}${Number(value || 0).toFixed(2)}`;
}

function ingredientName(item, fallback) {
  return item?.name || item?.generic_product_name || item?.product_name || fallback;
}

function ingredientMeta(item, t) {
  const quantity = item?.quantity ?? item?.qty_needed_base ?? item?.total_quantity;
  const unit = item?.unit || item?.base_unit || "";
  return quantity !== undefined && quantity !== null && quantity !== ""
    ? t("smart_kitchen.split_meals.quantity_unit", { quantity, unit })
    : t("smart_kitchen.empty_value");
}

function recipeLabel(recipe, fallback = "") {
  return String(recipe?.name || recipe?.title || recipe?.recipe_name || fallback || "").trim();
}

function recipeId(recipe) {
  return recipe?.id ?? recipe?.recipe_id ?? null;
}

function normalizeRecipeResults(payload) {
  const body = payload?.data || payload || {};
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.recipes)) return body.recipes;
  if (Array.isArray(body.items)) return body.items;
  return [];
}

function splitMealPreviewErrorMessage(error, t) {
  const status = error?.response?.status;
  if (status === 401 || error?.isAuthFailure) {
    return {
      message: t("smart_kitchen.split_meals.login_required"),
      action: "login",
    };
  }
  return {
    message: t("smart_kitchen.split_meals.error"),
    action: null,
  };
}

function warningRecipeId(warning) {
  if (warning?.recipe_id || warning?.recipeId) return warning.recipe_id || warning.recipeId;
  const match = String(warning?.message || "").match(/recipe\s+(\d+)/i);
  return match?.[1] || "";
}

function warningText(warning, t) {
  if (warning?.code === "recipe_has_no_products") {
    return t("smart_kitchen.split_meals.warning_recipe_missing_mapping", {
      recipeId: warningRecipeId(warning) || t("smart_kitchen.empty_value"),
    });
  }
  return warning?.friendly_message || warning?.message || warning?.code || t("smart_kitchen.split_meals.warning_fallback");
}

function IngredientList({ title, items = [], emptyLabel, testId }) {
  const { t } = useTranslation();
  return (
    <Box data-testid={testId}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
        {title}
      </Typography>
      {items.length ? (
        <List dense disablePadding>
          {items.map((item, index) => (
            <ListItem key={`${ingredientName(item, "item")}-${index}`} disableGutters>
              <ListItemText
                primary={ingredientName(item, t("smart_kitchen.split_meals.unknown_item"))}
                secondary={ingredientMeta(item, t)}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      )}
    </Box>
  );
}

function RecipeSearchPicker({
  slot,
  title,
  selectedRecipe,
  pickerState,
  onInputChange,
  onSearch,
  onSelect,
  onClear,
}) {
  const { t } = useTranslation();
  const options = pickerState.options || [];
  const selectedName = recipeLabel(selectedRecipe);

  return (
    <Stack spacing={1.25} data-testid={`split-meals-${slot}-recipe-picker`}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
        {title}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "flex-start" }}>
        <Autocomplete
          value={selectedRecipe}
          inputValue={pickerState.query}
          options={options}
          loading={pickerState.loading}
          sx={{ flex: 1 }}
          getOptionLabel={(option) => recipeLabel(option, t("smart_kitchen.split_meals.unknown_recipe"))}
          isOptionEqualToValue={(option, value) => String(recipeId(option)) === String(recipeId(value))}
          onInputChange={(_event, value, reason) => {
            if (reason !== "reset") onInputChange(slot, value);
          }}
          onChange={(_event, value) => onSelect(slot, value)}
          noOptionsText={
            pickerState.error
              ? t("smart_kitchen.split_meals.recipe_search_error")
              : pickerState.searched
                ? t("smart_kitchen.split_meals.no_recipe_results")
                : t("smart_kitchen.split_meals.recipe_search_empty")
          }
          loadingText={t("smart_kitchen.split_meals.recipe_search_loading")}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={t("smart_kitchen.split_meals.recipe_search_label")}
              placeholder={t("smart_kitchen.split_meals.recipe_search_placeholder")}
              inputProps={{
                ...params.inputProps,
                "data-testid": `split-meals-${slot}-recipe-search`,
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={`${slot}-${recipeId(option) || recipeLabel(option)}`}>
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {recipeLabel(option, t("smart_kitchen.split_meals.unknown_recipe"))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("smart_kitchen.split_meals.recipe_result_meta", { id: recipeId(option) || t("smart_kitchen.empty_value") })}
                </Typography>
              </Stack>
            </li>
          )}
        />
        <Button
          variant="outlined"
          onClick={() => onSearch(slot, pickerState.query)}
          disabled={pickerState.loading || pickerState.query.trim().length < 2}
          data-testid={`split-meals-${slot}-recipe-search-button`}
          sx={{ minHeight: 56 }}
        >
          {pickerState.loading ? t("smart_kitchen.split_meals.recipe_search_loading") : t("smart_kitchen.split_meals.recipe_search_button")}
        </Button>
      </Stack>
      {selectedName ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
          <Chip
            color="primary"
            variant="outlined"
            label={t("smart_kitchen.split_meals.selected_recipe", { name: selectedName })}
            data-testid={`split-meals-${slot}-selected-recipe`}
          />
          <Button size="small" variant="text" onClick={() => onClear(slot)} data-testid={`split-meals-${slot}-clear-recipe`}>
            {t("smart_kitchen.split_meals.clear_recipe")}
          </Button>
        </Stack>
      ) : null}
      {pickerState.error ? (
        <Alert severity="error" data-testid={`split-meals-${slot}-search-error`}>
          {pickerState.error}
        </Alert>
      ) : null}
    </Stack>
  );
}

export default function FamilySplitMealsPreview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { country = "en" } = useParams();
  const [form, setForm] = useState({
    mode: "child_alternative",
    meal_date: todayIso(),
    meal_type: "dinner",
    primary_recipe_id: "",
    primary_recipe_name: "",
    primary_servings: "2",
    primary_group_label: "adults",
    alternative_recipe_id: "",
    alternative_recipe_name: "",
    alternative_servings: "2",
    alternative_group_label: "children",
    reason: "",
  });
  const [selectedRecipes, setSelectedRecipes] = useState({ primary: null, alternative: null });
  const [recipePickers, setRecipePickers] = useState({
    primary: { query: "", options: [], loading: false, error: "", searched: false },
    alternative: { query: "", options: [], loading: false, error: "", searched: false },
  });
  const [manualIdsOpen, setManualIdsOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const primaryRecipeQuery = recipePickers.primary.query;
  const alternativeRecipeQuery = recipePickers.alternative.query;
  const usesAlternative = form.mode !== "same_meal_for_all";
  const previewData = preview?.data || preview || {};
  const warnings = previewData.warnings || [];
  const totalServings = previewData.total_servings_by_recipe || {};
  const currency = previewData.currency || "EUR";
  const combinedItems = previewData.combined_items || previewData.items || [];
  const sharedItems = previewData.shared_ingredients || [];
  const extraItems = previewData.extra_ingredients || [];
  const sharedIngredientCount = previewData.shared_ingredient_count ?? sharedItems.length;
  const extraIngredientCount = previewData.extra_ingredient_count ?? extraItems.length;
  const shoppingImpact = previewData.shopping_impact || {};
  const additionalProductsNeeded = shoppingImpact.additional_products_needed ?? extraIngredientCount;
  const estimatedTotalCost = previewData.estimated_total_cost;
  const estimatedExtraCost = previewData.estimated_extra_cost;
  const estimatedSharedSavings = previewData.estimated_shared_savings ?? previewData.savings_opportunity?.estimated_shared_savings;
  const savingsMessageKey = previewData.savings_opportunity?.message_key || (
    sharedIngredientCount > 0
      ? "smart_kitchen.split_meals.shared_savings_generic"
      : "smart_kitchen.split_meals.no_shared_savings"
  );

  const payload = useMemo(() => {
    const primary = {
      recipe_id: numberOrNull(form.primary_recipe_id),
      recipe_name: form.primary_recipe_name || undefined,
      servings: numberOrNull(form.primary_servings) || 1,
      target: {
        group_label: form.primary_group_label,
        member_count: numberOrNull(form.primary_servings) || 1,
      },
    };
    const alternative = {
      role: form.mode,
      recipe_id: numberOrNull(form.alternative_recipe_id),
      recipe_name: form.alternative_recipe_name || undefined,
      servings: numberOrNull(form.alternative_servings) || 1,
      target: {
        group_label: form.alternative_group_label,
        member_count: numberOrNull(form.alternative_servings) || 1,
      },
      reason: form.reason || undefined,
    };
    return {
      schema_version: "009Q.v1",
      meal_date: form.meal_date,
      meal_type: form.meal_type,
      mode: form.mode,
      primary,
      alternatives: usesAlternative ? [alternative] : [],
      currency: "EUR",
    };
  }, [form, usesAlternative]);

  const searchRecipes = useCallback(async (slot, rawQuery) => {
    const query = String(rawQuery || "").trim();
    if (query.length < 2) {
      setRecipePickers((current) => ({
        ...current,
        [slot]: {
          ...current[slot],
          options: [],
          loading: false,
          error: "",
          searched: false,
        },
      }));
      return;
    }

    setRecipePickers((current) => ({
      ...current,
      [slot]: { ...current[slot], loading: true, error: "", searched: true },
    }));
    try {
      const response = await api.recipes.findByName(query);
      const options = normalizeRecipeResults(response);
      setRecipePickers((current) => ({
        ...current,
        [slot]: { ...current[slot], options, loading: false, error: "", searched: true },
      }));
    } catch (err) {
      setRecipePickers((current) => ({
        ...current,
        [slot]: {
          ...current[slot],
          options: [],
          loading: false,
          error: err?.response?.data?.message || err?.message || t("smart_kitchen.split_meals.recipe_search_error"),
          searched: true,
        },
      }));
    }
  }, [t]);

  useEffect(() => {
    const scheduleSearch = (slot, rawQuery) => {
      const query = rawQuery.trim();
      if (query.length < 2) return null;

      return setTimeout(() => searchRecipes(slot, query), 300);
    };

    const timers = [
      scheduleSearch("primary", primaryRecipeQuery),
      scheduleSearch("alternative", alternativeRecipeQuery),
    ];

    return () => {
      timers.forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [primaryRecipeQuery, alternativeRecipeQuery, searchRecipes]);

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const updateRecipeSearch = (slot, query) => {
    setRecipePickers((current) => ({
      ...current,
      [slot]: {
        ...current[slot],
        query,
        ...(query.trim().length < 2
          ? { options: [], loading: false, error: "", searched: false }
          : {}),
      },
    }));
  };

  const selectRecipe = (slot, recipe) => {
    const id = recipeId(recipe);
    const name = recipeLabel(recipe);
    setSelectedRecipes((current) => ({ ...current, [slot]: recipe }));
    setRecipePickers((current) => ({
      ...current,
      [slot]: { ...current[slot], query: name, error: "" },
    }));
    setForm((current) => ({
      ...current,
      [`${slot}_recipe_id`]: id ? String(id) : "",
      [`${slot}_recipe_name`]: name,
    }));
  };

  const clearRecipe = (slot) => {
    setSelectedRecipes((current) => ({ ...current, [slot]: null }));
    setRecipePickers((current) => ({
      ...current,
      [slot]: { query: "", options: [], loading: false, error: "", searched: false },
    }));
    setForm((current) => ({
      ...current,
      [`${slot}_recipe_id`]: "",
      [`${slot}_recipe_name`]: "",
    }));
  };

  const runPreview = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const response = await api.smartKitchen.previewSplitMeal(payload);
      setPreview(response?.data || {});
    } catch (err) {
      setError(splitMealPreviewErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={3}>
        <Box>
          <Chip
            icon={<PreviewRoundedIcon />}
            label={t("smart_kitchen.split_meals.experimental")}
            variant="outlined"
            sx={{ mb: 1.5 }}
          />
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {t("smart_kitchen.split_meals.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
            {t("smart_kitchen.split_meals.subtitle")}
          </Typography>
        </Box>

        <Alert severity="info">
          <Stack spacing={0.5}>
            <Typography variant="body2">{t("smart_kitchen.split_meals.family_copy")}</Typography>
            <Typography variant="body2">{t("smart_kitchen.split_meals.preview_copy")}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>{t("smart_kitchen.split_meals.no_save_copy")}</Typography>
          </Stack>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 1 }}>
              <CardContent>
                <Stack component="form" spacing={2} onSubmit={runPreview}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <RestaurantMenuRoundedIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      {t("smart_kitchen.split_meals.form_title")}
                    </Typography>
                  </Stack>
                  <TextField
                    select
                    fullWidth
                    label={t("smart_kitchen.split_meals.mode")}
                    value={form.mode}
                    onChange={update("mode")}
                    inputProps={{ "data-testid": "split-meals-mode" }}
                  >
                    {MODES.map((mode) => (
                      <MenuItem key={mode} value={mode}>
                        {t(`smart_kitchen.split_meals.modes.${mode}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label={t("smart_kitchen.split_meals.meal_date")} type="date" value={form.meal_date} onChange={update("meal_date")} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField select fullWidth label={t("smart_kitchen.split_meals.meal_type")} value={form.meal_type} onChange={update("meal_type")}>
                        {MEAL_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {t(`smart_kitchen.split_meals.meal_types.${type}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Divider />
                  <RecipeSearchPicker
                    slot="primary"
                    title={t("smart_kitchen.split_meals.primary_recipe")}
                    selectedRecipe={selectedRecipes.primary}
                    pickerState={recipePickers.primary}
                    onInputChange={updateRecipeSearch}
                    onSearch={searchRecipes}
                    onSelect={selectRecipe}
                    onClear={clearRecipe}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField required fullWidth label={t("smart_kitchen.split_meals.servings")} value={form.primary_servings} onChange={update("primary_servings")} inputProps={{ inputMode: "numeric" }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField select fullWidth label={t("smart_kitchen.split_meals.target_group")} value={form.primary_group_label} onChange={update("primary_group_label")}>
                        {GROUPS.map((group) => (
                          <MenuItem key={group} value={group}>
                            {t(`smart_kitchen.split_meals.groups.${group}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {usesAlternative ? (
                    <>
                      <Divider />
                      <RecipeSearchPicker
                        slot="alternative"
                        title={t("smart_kitchen.split_meals.alternative_recipe")}
                        selectedRecipe={selectedRecipes.alternative}
                        pickerState={recipePickers.alternative}
                        onInputChange={updateRecipeSearch}
                        onSearch={searchRecipes}
                        onSelect={selectRecipe}
                        onClear={clearRecipe}
                      />
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField required fullWidth label={t("smart_kitchen.split_meals.servings")} value={form.alternative_servings} onChange={update("alternative_servings")} inputProps={{ inputMode: "numeric" }} />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField select fullWidth label={t("smart_kitchen.split_meals.target_group")} value={form.alternative_group_label} onChange={update("alternative_group_label")}>
                            {GROUPS.map((group) => (
                              <MenuItem key={group} value={group}>
                                {t(`smart_kitchen.split_meals.groups.${group}`)}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField fullWidth label={t("smart_kitchen.split_meals.reason_optional")} value={form.reason} onChange={update("reason")} />
                        </Grid>
                      </Grid>
                    </>
                  ) : null}

                  <Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setManualIdsOpen((open) => !open)}
                      data-testid="split-meals-manual-id-toggle"
                    >
                      {manualIdsOpen ? t("smart_kitchen.split_meals.hide_manual_ids") : t("smart_kitchen.split_meals.show_manual_ids")}
                    </Button>
                    <Collapse in={manualIdsOpen} unmountOnExit>
                      <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={usesAlternative ? 6 : 12}>
                          <TextField
                            fullWidth
                            label={t("smart_kitchen.split_meals.primary_recipe_id")}
                            value={form.primary_recipe_id}
                            onChange={update("primary_recipe_id")}
                            inputProps={{ inputMode: "numeric", "data-testid": "split-meals-primary-manual-id" }}
                          />
                        </Grid>
                        {usesAlternative ? (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label={t("smart_kitchen.split_meals.alternative_recipe_id")}
                              value={form.alternative_recipe_id}
                              onChange={update("alternative_recipe_id")}
                              inputProps={{ inputMode: "numeric", "data-testid": "split-meals-alternative-manual-id" }}
                            />
                          </Grid>
                        ) : null}
                      </Grid>
                    </Collapse>
                  </Box>

                  <Button type="submit" variant="contained" disabled={loading} data-testid="split-meals-preview-button">
                    {loading ? t("smart_kitchen.split_meals.previewing") : t("smart_kitchen.split_meals.preview_button")}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 1, minHeight: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  {t("smart_kitchen.split_meals.preview_title")}
                </Typography>
                {error ? (
                  <Alert
                    severity={error.action === "login" ? "info" : "error"}
                    sx={{ mb: 2 }}
                    action={error.action === "login" ? (
                      <Button color="inherit" size="small" onClick={() => navigate(`/${country}/login`)}>
                        {t("smart_kitchen.split_meals.login_action")}
                      </Button>
                    ) : null}
                  >
                    {error.message}
                  </Alert>
                ) : null}
                {!preview && !error ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("smart_kitchen.split_meals.preview_empty")}
                  </Typography>
                ) : null}
                {preview ? (
                  <Stack spacing={2.5} data-testid="split-meals-preview-result">
                    {preview.success === false ? (
                      <Alert severity="warning">
                        {(preview.errors || []).map((item) => item.message).join(" ")}
                      </Alert>
                    ) : null}
                    <Box
                      data-testid="split-meals-impact-summary"
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 2,
                        bgcolor: "background.default",
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        {t("smart_kitchen.split_meals.impact_summary_title")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t("smart_kitchen.split_meals.cost_insight_copy")}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        <Chip label={t("smart_kitchen.split_meals.shared_count", { count: sharedIngredientCount })} />
                        <Chip label={t("smart_kitchen.split_meals.extra_count", { count: extraIngredientCount })} />
                        <Chip color={additionalProductsNeeded > 0 ? "primary" : "default"} label={t("smart_kitchen.split_meals.additional_products_count", { count: additionalProductsNeeded })} />
                      </Stack>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box data-testid="split-meals-cost-summary">
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                            {t("smart_kitchen.split_meals.cost_summary")}
                          </Typography>
                          <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between" gap={2}>
                              <Typography variant="body2" color="text.secondary">{t("smart_kitchen.split_meals.total_cost")}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                {estimatedTotalCost !== null && estimatedTotalCost !== undefined
                                  ? formatMoney(estimatedTotalCost, currency)
                                  : t("smart_kitchen.empty_value")}
                              </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" gap={2}>
                              <Typography variant="body2" color="text.secondary">{t("smart_kitchen.split_meals.extra_cost")}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                {estimatedExtraCost !== null && estimatedExtraCost !== undefined
                                  ? `+${formatMoney(estimatedExtraCost, currency)}`
                                  : t("smart_kitchen.empty_value")}
                              </Typography>
                            </Stack>
                          </Stack>
                          {(estimatedTotalCost === null || estimatedTotalCost === undefined || estimatedExtraCost === null || estimatedExtraCost === undefined) ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              {t("smart_kitchen.split_meals.cost_data_helper")}
                            </Typography>
                          ) : null}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box data-testid="split-meals-savings-summary">
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                            {t("smart_kitchen.split_meals.savings_opportunity")}
                          </Typography>
                          {estimatedSharedSavings !== null && estimatedSharedSavings !== undefined ? (
                            <Typography variant="body2">
                              {t("smart_kitchen.split_meals.shared_savings_amount", { value: formatMoney(estimatedSharedSavings, currency) })}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t(savingsMessageKey)}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                    <Box data-testid="split-meals-shopping-impact">
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                        {t("smart_kitchen.split_meals.shopping_impact")}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <Chip sx={{ width: "100%" }} label={t("smart_kitchen.split_meals.shared_count", { count: sharedIngredientCount })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Chip sx={{ width: "100%" }} label={t("smart_kitchen.split_meals.extra_count", { count: extraIngredientCount })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Chip sx={{ width: "100%" }} label={t("smart_kitchen.split_meals.additional_products_count", { count: additionalProductsNeeded })} />
                        </Grid>
                      </Grid>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">{t("smart_kitchen.split_meals.total_servings")}</Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                        {Object.keys(totalServings).length ? Object.entries(totalServings).map(([recipeId, servings]) => (
                          <Chip key={recipeId} size="small" label={t("smart_kitchen.split_meals.recipe_servings", { recipeId, servings })} />
                        )) : <Typography variant="body2">{t("smart_kitchen.empty_value")}</Typography>}
                      </Stack>
                    </Box>
                    <Divider />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <IngredientList
                          title={t("smart_kitchen.split_meals.combined_items")}
                          items={combinedItems}
                          emptyLabel={t("smart_kitchen.split_meals.no_combined_items")}
                          testId="split-meals-combined-items"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Stack spacing={2}>
                          <IngredientList
                            title={t("smart_kitchen.split_meals.shared_ingredients")}
                            items={sharedItems}
                            emptyLabel={t("smart_kitchen.split_meals.no_shared_items")}
                            testId="split-meals-shared-items"
                          />
                          <IngredientList
                            title={t("smart_kitchen.split_meals.extra_ingredients")}
                            items={extraItems}
                            emptyLabel={t("smart_kitchen.split_meals.no_extra_items")}
                            testId="split-meals-extra-items"
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                    <Divider />
                    <Box data-testid="split-meals-warnings">
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                        {t("smart_kitchen.split_meals.warnings")}
                      </Typography>
                      {warnings.length ? (
                        <Stack spacing={1}>
                          {warnings.map((warning, index) => (
                            <Alert key={`${warning.code || "warning"}-${index}`} severity="info">
                              {warningText(warning, t)}
                            </Alert>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("smart_kitchen.split_meals.no_warnings")}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
